import type { ConditionGroup } from "~/components/dashboard/PageTable/AdvancedConditionsModal";

type PagesListQueryParams = {
  pageSize: number;
  pageNumber: number;
  sorting: string[];
  searchValue?: string;
  selectedStatuses: string[];
  selectedPageTypes: string[];
  selectedCustomizations: string[];
  advancedConditions?: ConditionGroup[];
};

export const queryKeys = {
  siteDetails: (siteId?: number | string) =>
    [
      "site",
      "siteDetails-2",
      siteId == null ? "none" : Number(siteId),
    ] as const,

  pagesRoot: (siteId: string | number) => ["pages", Number(siteId)] as const,

  pagesList: (siteId: number | string, params: PagesListQueryParams) =>
    [
      "pages",
      Number(siteId),
      params.pageSize,
      params.pageNumber,
      params.sorting.join(","),
      params.searchValue ?? "",
      params.selectedStatuses.join(","),
      params.selectedPageTypes.join(","),
      params.selectedCustomizations.join(","),
      JSON.stringify(params.advancedConditions ?? []),
    ] as const,

  siteVisibilityReports: (siteId?: number | string) =>
    [
      "site_visibility_reports",
      siteId == null ? "none" : Number(siteId),
    ] as const,

  pageTypesDetailRoot: (siteId: number | string) =>
    ["pageTypesDetail", String(siteId)] as const,

  pageTypesDetail: (siteId: number | string, selectedStatuses: string[]) =>
    [...queryKeys.pageTypesDetailRoot(siteId), { selectedStatuses }] as const,

  pageStatusesDetailRoot: (siteId: number | string) =>
    ["pageStatusesDetail", String(siteId)] as const,

  pageStatusesDetail: (siteId: number | string, selectedPageTypes: string[]) =>
    [
      ...queryKeys.pageStatusesDetailRoot(siteId),
      { selectedPageTypes },
    ] as const,

  team: (teamId?: number | string | null) =>
    ["teams", teamId == null ? "none" : Number(teamId)] as const,

  crsRules: () => ["crsRules"] as const,

  wafDirectives: (siteId: number | string) =>
    ["wafDirectives", Number(siteId)] as const,

  exportsRoot: (siteId: number | string) =>
    ["exports", Number(siteId)] as const,

  exports: (siteId: number | string, exportType: string) =>
    [...queryKeys.exportsRoot(siteId), exportType] as const,

  clientReportSchedule: (siteId: number | string) =>
    ["clientReportSchedule", Number(siteId)] as const,

  clientReportRecipients: (siteId: number | string) =>
    ["clientReportRecipients", Number(siteId)] as const,

  reportRecipientLists: (teamId?: number | string | null) =>
    ["reportRecipientLists", teamId == null ? "none" : Number(teamId)] as const,

  // Every member of the team on one page, unlike the paged Team Members table.
  teamMembershipsAll: (teamId?: number | string | null) =>
    ["teamMembershipsAll", teamId == null ? "none" : Number(teamId)] as const,

  notificationsRoot: (teamId: number) => ["notifications", teamId] as const,

  notifications: (teamId: number, onlyUnread: boolean = false) =>
    ["notifications", teamId, { onlyUnread }] as const,

  siteCustomizationActions: (siteId: number | string) =>
    ["siteCustomizationActions", Number(siteId)] as const,

  generateCustomizationsCount: (
    siteId: number | string,
    params: Omit<
      PagesListQueryParams,
      "pageSize" | "pageNumber" | "sorting"
    > & {
      selectedIds?: number[];
      variants?: string[];
    },
  ) =>
    [
      "generateCustomizationsCount",
      Number(siteId),
      params.searchValue ?? "",
      params.selectedStatuses.join(","),
      params.selectedPageTypes.join(","),
      params.selectedCustomizations.join(","),
      JSON.stringify(params.advancedConditions ?? []),
      (params.selectedIds ?? []).join(","),
      (params.variants ?? []).join(","),
    ] as const,

  generateCustomizationsSelectedCount: (
    siteId: number | string,
    params: Omit<
      PagesListQueryParams,
      "pageSize" | "pageNumber" | "sorting"
    > & {
      selectedIds?: number[];
    },
  ) =>
    [
      "generateCustomizationsSelectedCount",
      Number(siteId),
      params.searchValue ?? "",
      params.selectedStatuses.join(","),
      params.selectedPageTypes.join(","),
      params.selectedCustomizations.join(","),
      JSON.stringify(params.advancedConditions ?? []),
      (params.selectedIds ?? []).join(","),
    ] as const,

  pendingStatusesAndPermissions: (group: string) =>
    ["pendingStatusesAndPermissions", group] as const,

  contentAutomationsRoot: (group: string, pageId?: number) =>
    ["contentAutomations", group, pageId] as const,

  contentAutomations: (
    group: string,
    pageId?: number,
    sourceFilter?: "auto" | "manual",
    outdatedFilter?: boolean,
  ) =>
    [
      ...queryKeys.contentAutomationsRoot(group, pageId),
      sourceFilter,
      outdatedFilter,
    ] as const,

  widgetCustomizationsStatsRoot: (group: string) =>
    ["widgetCustomizationsStats", group] as const,

  widgetCustomizationsStats: (
    group: string,
    sourceFilter?: "auto" | "manual",
    outdatedFilter?: boolean,
  ) =>
    [
      ...queryKeys.widgetCustomizationsStatsRoot(group),
      sourceFilter,
      outdatedFilter,
    ] as const,

  customizedPagesRoot: (group: string) => ["customizedPages", group] as const,

  customizedPages: (
    group: string,
    page: number,
    searchTerm: string,
    sourceFilter?: "auto" | "manual",
    outdatedFilter?: boolean,
  ) =>
    [
      ...queryKeys.customizedPagesRoot(group),
      page,
      searchTerm,
      sourceFilter,
      outdatedFilter,
    ] as const,

  pageMetasRoot: (siteId: number, pageId: number) =>
    ["pageMetas", siteId, pageId] as const,
  pageMetas: (siteId: number, pageId: number, kind: string) =>
    [...queryKeys.pageMetasRoot(siteId, pageId), kind] as const,

  mfaStatus: (token: string | null) => ["mfaStatus", token] as const,
  // Partial-match root — covers every token-scoped variant in cache.
  mfaStatusRoot: () => ["mfaStatus"] as const,

  // Token-scoped so session data never leaks across auth contexts on
  // login/logout/impersonation token rotation.
  session: (token: string | null) => ["session", token] as const,
  sessionRoot: () => ["session"] as const,

  authUser: (token: string | null) => ["authUser", token] as const,
  authUserRoot: () => ["authUser"] as const,

  // Whether an activation link is still to be used (not the link itself:
  // SubscribePage keeps that under ["subscriptionLink", code]).
  subscriptionLinkStatus: (code: string | undefined) =>
    ["subscriptionLinkStatus", code] as const,
};
